import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  FieldArray,
  Form,
  Formik,
  FieldProps,
  getIn,
} from "formik";
import * as yup from "yup";
import { shallow } from "zustand/shallow";

// === UI Components ===
import { InputText, InputTextArea } from "../../../../common/UI/Inputs";
import AppGetIcon from "../../../../common/UI/AppGetIcon";
import DataTable from "../../../../common/UI/DataTable/dataTable";
import Modal from "../../../../common/UI/Modal";
import ColorField from "../../../../common/UI/Inputs/ColorField";
import FileInput from "../../../../common/UI/Inputs/FileInput";

// === Stores ===
import { useHeaderStore } from "../../../../store/header.store";

// === Toast ===
import { toast } from "react-toastify";

// === Estilos ===
import style from "./Widgets.module.css";
import styleForm from "../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../common/UI/DataTable/dataTable.module.css";

import { GetWidgetsWithPaginationService } from "../../services/widgets/getWidgetsWithPagination.service";
import { CreateOrUpdatedWidgetsService } from "../../services/widgets/createOrUpdateWidgets.service";
import { DeleteWidgetsService } from "../../services/widgets/deleteWidgets.service";
import { GetInstitutionsWithPaginationService } from "../../services/institutions/getInstitutionsWithPagination.service";
import AppInputSearch from "../../../../common/UI/Inputs/appInputSearch";
import { GetReactionsService } from "../../services/widgets/getReactions.service";
import { CreateOrUpdateUploadReactionsService } from "../../services/widgets/createOrUpdateUploadReactions.service";
import { DeleteUploadReactionService } from "../../services/widgets/deleteUploadReaction.service";
import DeleteConfirmationModal from "../../../../common/UI/DataTable/DeleteConfirmationModal/deleteConfirmationModal";

const getInstitutionsWithPaginationService = new GetInstitutionsWithPaginationService();

const getWidgetsWithPaginationService = new GetWidgetsWithPaginationService();
const createOrUpdatedWidgetsService = new CreateOrUpdatedWidgetsService();
const deleteWidgetsService = new DeleteWidgetsService();

const createOrUpdateUploadReactionsService = new CreateOrUpdateUploadReactionsService();
const deleteUploadReactionService = new DeleteUploadReactionService();

// ===================== Tipos =====================
export interface EmotionItem {
  id: number;
  name: string;
  defaultImageUrl?: string;
  defaultMessage?: string;
  message: string;
  imageUrl?: string;
  reactionId?: number;
  reaction?: any;
}

export interface Institution {
  id: number;
  name: string;
}

export interface WidgetData {
  id: number | null;
  widgetId: string,
  institutionId: string;
  institution: Institution;
  name: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  // welcomeMessage: string;
  // goodbyeMessage: string;
  reactions: EmotionItem[];
  isActive: any;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface WidgetListResponse {
  data: WidgetData[];
  pagination: Pagination;
}

// El InputSearch espera value: string
type DataSelectDto = { label: string; value: string; raw?: any };
type OptionType = DataSelectDto | null;




// ===================== Yup Schema =====================
const WidgetSchema = yup.object().shape({
  id: yup.number().nullable(),
  institutionId: yup.number().required("Institución es requerida"),
  name: yup.string().required("Nombre es requerido"),
  description: yup
    .string()
    .required("Descripción es requerida")
    .max(500, "La descripción no puede superar los 500 caracteres"),
  primaryColor: yup
    .string()
    .matches(/^#([0-9A-F]{3}){1,2}$/i, "Color inválido")
    .required("Color primario es requerido"),
  secondaryColor: yup
    .string()
    .matches(/^#([0-9A-F]{3}){1,2}$/i, "Color inválido")
    .required("Color secundario es requerido"),
  // welcomeMessage: yup.string().required("Mensaje de bienvenida requerido"),
  // goodbyeMessage: yup.string().required("Mensaje de despedida requerido"),
  isActive: yup.boolean(),
  
});



// ===================== Componente =====================
export default function WidgetsCrud() {
  const headerStore = useHeaderStore(
    (state) => ({ setModule: state.setModule, setModuleItem: state.setModuleItem }),
    shallow
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const [isOpenCurrentDelete, setIsOpenCurrentDelete] = useState<any>(null);
  const [showConfirmExitModal, setShowConfirmExitModal] = useState(false);
  const [pendingExitValues, setPendingExitValues] = useState<WidgetData | null>(null);
  const [reopenFormOnCancelExit, setReopenFormOnCancelExit] = useState(false);
  const [originalEditData, setOriginalEditData] = useState<WidgetData | null>(null);

  const [hoverEdit, setHoverEdit] = useState<number | null>(null);
  const [hoverDelete, setHoverDelete] = useState<number | null>(null);
  const [hoverHtml, setHoverHtml] = useState<number | null>(null);
  const [loadingEmotions, setLoadingEmotions] = useState<Record<number, boolean>>({});
  const formikValuesRef = useRef<WidgetData | null>(null);

  // --- al inicio de WidgetsCrud (estados nuevos) ---
  const [isOpenHtml, setIsOpenHtml] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState<string>("");
  const [htmlWidgetName, setHtmlWidgetName] = useState<string>("");

  // Opcional: parametriza las URLs (puedes moverlas a .env)
  const WIDGET_CSS_URL = `${process.env.REACT_APP_WIDGET_URL}/chatbot-widget.css`;
  const WIDGET_UMD_URL = `${process.env.REACT_APP_WIDGET_URL}/widget.umd.js`;


  // Genera el HTML usando el widgetId
  const generateEmbedHtml = (widgetId: string) => `<!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Chatbot Widget</title>
    <link rel="stylesheet" href="${WIDGET_CSS_URL}" />
  </head>
  <body>
    <div id="my-widget"></div>
    <script src="${WIDGET_UMD_URL}"></script>
    <script>
      // Inicializa el chatbot en el div #my-widget
      window.initChatbot('my-widget', {
        widgetId: '${widgetId}'
      });
    </script>
  </body>
  </html>`;

  // Abre el modal con el HTML para el widget seleccionado
  const openHtmlModal = (row: WidgetData) => {
    setHtmlPreview(generateEmbedHtml(String(row.widgetId)));
    setHtmlWidgetName(row.name || `widget-${row.widgetId}`);
    setIsOpenHtml(true);
  };

  // Utilidades para copiar / descargar
  const copyHtmlToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(htmlPreview);
      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" /> HTML copiado al portapapeles
        </div>,
        { icon: false }
      );
    } catch {
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" /> No se pudo copiar el HTML
        </div>,
        { icon: false }
      );
    }
  };

  const downloadHtml = () => {
    const blob = new Blob([htmlPreview], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${htmlWidgetName || "widget"}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const initialForm: WidgetData = useMemo(
    () => ({
      id: null,
      institutionId: '',
      widgetId: '',
      institution: { id: 0, name: '' },
      name: "",
      description: "",
      primaryColor: "#000000",
      secondaryColor: "#000000",
      // welcomeMessage: "",
      // goodbyeMessage: "",
      reactions: ([]),
      isActive: true,
    }),
    []
  );

  const [formData, setFormData] = useState<WidgetData>(initialForm);
  const [selectedInstitution, setSelectedInstitution] = useState({
    label: '',
    value: ''
  });


  // Header info
  useEffect(() => {
    headerStore.setModule("Administración", "");
    headerStore.setModuleItem("Widgets", "/admin/widgets");
  }, [headerStore]);


  const serviceInstitutionsAllFilter = async (value: any) => {
    let params: any = {
      search: value,
    };
    let response: any = await getInstitutionsWithPaginationService.run(params);
    return response.data.map((item: any) => {

      return {
        label: item.name,
        value: item.id
      };

    });
  };
  const openCreateModal = async () => {
    const response = await GetReactionsService();
    const normalizedEmotions = mapReactionsToEmotions(response);
    setFormData({ ...initialForm, reactions: normalizedEmotions });
    setIsEditMode(false);
    setIsOpen(true);
  };


  const handleEdit = (row: WidgetData) => {
    const mappedReactions = row.reactions.map((emo) => ({
      id: emo.id ?? emo.reactionId,
      reactionId: emo.reactionId,
      name: emo.name,
      defaultImageUrl: emo.defaultImageUrl ?? "",
      defaultMessage: emo.defaultMessage,
      imageUrl: emo.imageUrl ?? "",
      message: emo.message,
      reaction: emo.reaction,
    }));

    const normalizedData = {
      ...row,
      isActive: row.isActive == 1 ? true : false,
      reactions: mappedReactions,
    };

    setSelectedInstitution({
      label: row.institution?.name ?? "",
      value: row.institutionId,
    });

    setFormData(normalizedData);
    setOriginalEditData(JSON.parse(JSON.stringify(normalizedData))); // ← importante: deep clone
    setIsEditMode(true);
    setIsOpen(true);
  };


  const handleCloseModal = (values?: WidgetData) => {
    if (values && hasUploadedImages(values)) {
      setPendingExitValues(values);
      setShowConfirmExitModal(true); // muestra el modal de confirmación
    } else {
      closeModalCompletely();
    }
  };

  const closeModalCompletely = () => {
    setIsOpen(false);
    setFormData(initialForm);
    setIsEditMode(false);
    setOriginalEditData(null); // ← limpiar también
  };


  const handleExitAndDeleteUploaded = async () => {
    if (!pendingExitValues) return;

    const updatedReactions = [...pendingExitValues.reactions];

    for (const emo of updatedReactions) {
      if (emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl) {
        try {
          const response: any = await deleteUploadReactionService.run(emo.imageUrl);
          emo.imageUrl = undefined;

          toast.success(
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <AppGetIcon name="checkCircleFill" />
              {response?.message ?? "Archivo eliminado correctamente"}
            </div>,
            { icon: false }
          );

        } catch (error: any) {
          console.error("❌ Error al eliminar archivo:", error);

          const errorMessage =
            error?.response?.data?.message || error?.message || "Ocurrió un error al eliminar el archivo";

          toast.error(
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <AppGetIcon name="xCircleFill" />
              {errorMessage}
            </div>,
            { icon: false }
          );
        }
      }
    }

    // Reset form
    setFormData(initialForm);
    setIsEditMode(false);
    setPendingExitValues(null);
    setShowConfirmExitModal(false);
    setReopenFormOnCancelExit(false);
  };



  const handleSubmit = async (values: WidgetData) => {
    setLoading(true);
    // Armado del payload final según el POST real
    const payload = {
      id: values.id ?? undefined,
      institutionId: values.institutionId,
      name: values.name,
      description: values.description,
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      // welcomeMessage: values.welcomeMessage,
      // goodbyeMessage: values.goodbyeMessage,
      isActive: values.isActive,
      font: "Arial", // puedes reemplazarlo si lo haces dinámico
      fontSize: "16px", // igual que arriba
      reactions: values.reactions.map((emo) => ({
        reactionId: emo.id,
        imageUrl: emo.imageUrl || emo.defaultImageUrl,
        message: emo.message || "", // ✅ Incluye aquí el mensaje
      })),
    };

    try {
      const result = await createOrUpdatedWidgetsService.run(payload, payload.id);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" /> {result.message ?? "Operación realizada correctamente"}
        </div>,
        { icon: false }
      );

      setIsReloadData((prev) => !prev);
      setSelectedInstitution({
        label: '',
        value: ''
      });
      setIsOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {msg}
        </div>,
        { icon: false }
      );
    } finally {
      setLoading(false);
    }
  };
  const handleCancelExit = () => {
    setShowConfirmExitModal(false);

    if (reopenFormOnCancelExit && pendingExitValues) {
      setFormData(pendingExitValues);
      setIsEditMode(!!pendingExitValues.id);
      setIsOpen(true); // volver a abrir
    }
  };

  const handleDelete = async () => {

    setLoading(true)
    try {
      const response: any = await deleteWidgetsService.run(isOpenCurrentDelete?.id,);
      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="checkCircleFill" />
          {response.message}
        </div>,
        { icon: false }
      );

      setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación
      setIsOpenCurrentDelete(null); // Cierra el modal
      setLoading(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      toast.error(
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
      console.error("Error al eliminar:", error);
      setLoading(false);
    }

  };
  const hasImageChangesComparedToInitialData = (current: WidgetData): boolean => {
    if (!originalEditData || !originalEditData.reactions?.length) return false;

    return current.reactions.some((currentReaction) => {
      const initialReaction = originalEditData.reactions.find((r) => r.id === currentReaction.id);
      return initialReaction && currentReaction.imageUrl !== initialReaction.imageUrl;
    });
  };


  // ===================== Columnas DataTable =====================
  const columns = [
    { header: "NOMBRE", field: "name" },
    { header: "ID WIDGET", field: "widgetId" },

    {
      header: "INSTITUCIÓN",
      field: "institutionsName",
      render: (row: WidgetData) => (
        <div style={{ display: "flex", gap: 4 }}>
          <span>{row?.institution?.name}</span>
        </div>
      ),
    },
    {
      header: "COLOR PRIMARIO",
      field: "colors",
      render: (row: WidgetData) => (
        <div style={{ display: "flex", gap: 4, width: 100 }}>
          <span
            title={`Primario ${row.primaryColor}`}
            style={{ width: 100, height: 18, background: row.primaryColor, borderRadius: 3 }}
          />
        </div>
      ),
    },
    {
      header: "COLOR SECUNDARIO",
      field: "colors",
      render: (row: WidgetData) => (
        <div style={{ display: "flex", gap: 4, width: 100 }}>
          <span
            title={`Secundario ${row.secondaryColor}`}
            style={{ width: 100, height: 18, background: row.secondaryColor, borderRadius: 3 }}
          />
        </div>
      ),
    },
    // {
    //   header: "MENSAJE BIENVENIDA",
    //   field: "welcomeMessage",
    //   render: (row: WidgetData) => (
    //     <div style={{ maxWidth: 240 }}>
    //       <div>{row.welcomeMessage}</div>
    //     </div>
    //   ),
    // },
    //     {
    //   header: "MENSAJE DESPEDIDA",
    //   field: "welcomeMessage",
    //   render: (row: WidgetData) => (
    //     <div style={{ maxWidth: 240 }}>
    //       <div> {row.goodbyeMessage}</div>
    //     </div>
    //   ),
    // },
    {
      header: "ESTADO",
      field: "isActive",
      render: (row: WidgetData) => <div>{row.isActive ? "Activo" : "Inactivo"}</div>,
    },
    {
      header: "OPCIONES",
      field: "",
      render: (row: WidgetData) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
          <button
            className={styleDataTable.buttom_option}
            onClick={() => handleEdit(row)}
            onMouseEnter={() => setHoverEdit(row.id || 0)}
            onMouseLeave={() => setHoverEdit(null)}
          >
            <AppGetIcon name="edit" classIcon={styleDataTable.sizeIcon} strokeColor="#1196ce"
              hoverColor={hoverEdit === row.id ? "#FFF" : undefined}
            />
            Editar
          </button>

          <button
            className={styleDataTable.buttom_option}
            onClick={() => { setIsOpenCurrentDelete(row); }}
            onMouseEnter={() => setHoverDelete(row.id || 0)}
            onMouseLeave={() => setHoverDelete(null)}
          >
            <AppGetIcon name="trash" classIcon={styleDataTable.sizeIcon} strokeColor="#1196ce"
              hoverColor={hoverDelete === row.id ? "#FFF" : ""}
            />
            Eliminar
          </button>

          {/* ← Nuevo botón */}
          <button
            className={styleDataTable.buttom_option}
            onClick={() => openHtmlModal(row)}
            onMouseEnter={() => setHoverHtml(row.id || 0)}
            onMouseLeave={() => setHoverHtml(null)}
          >

            <AppGetIcon name="html" classIcon={styleDataTable.sizeIcon} fillColor="#1196ce" hoverColor={hoverHtml === row.id ? "#FFF" : ""} />
            Generar HTML
          </button>
        </div>
      ),
    },

  ];
  const hasUploadedImages = (values: WidgetData) => {
    return values.reactions.some(
      (emo) => emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl
    );
  };
  const handleIntentToCloseModal = (values: WidgetData) => {
    setIsOpen(false); // cerrar el modal visualmente
    setSelectedInstitution({
      label: '',
      value: ''
    });
    const isEditingAndChanged = isEditMode && hasImageChangesComparedToInitialData(values);
    const hasNewUploads = !isEditMode && hasUploadedImages(values);

    if (isEditingAndChanged || hasNewUploads) {
      setPendingExitValues(values);
      setReopenFormOnCancelExit(true);
      setTimeout(() => setShowConfirmExitModal(true), 300);
    } else {
      closeModalCompletely();
    }
  };


  const handleCloseModalDelete = () => {
    // setInitialData(null);
    setIsOpenCurrentDelete(null);
  };
  const mapReactionsToEmotions = (reactions: any[]): EmotionItem[] => {
    return reactions.map((reaction) => ({
      id: reaction.id,
      name: reaction.name,
      defaultImageUrl: reaction.defaultImageUrl,
      message: reaction.message,
      imageUrl: reaction.imageUrl,
      reaction: reaction.reaction
    }));
  };


  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    emotionId: number,
    setFieldValue: (field: string, value: any) => void,
    values: WidgetData
  ) => {
    setLoadingEmotions(prev => ({ ...prev, [emotionId]: true }));

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const dataParams = { file };

      try {
        const response = await createOrUpdateUploadReactionsService.run(dataParams);
        const uploadedUrl = response?.data?.url;

        const updatedEmotions = values.reactions.map((emo) =>
          emo.id === emotionId ? { ...emo, imageUrl: uploadedUrl } : emo
        );
        // Actualizar Formik y formData
        setFieldValue("reactions", updatedEmotions);

        // ultimo ajuste sin probar
        // setFormData((prev) => ({
        //   ...prev,
        //   reactions: updatedEmotions,
        // }));

        toast.success(
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <AppGetIcon name="checkCircleFill" />
            {response.message}
          </div>,
          { icon: false }
        );
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        toast.error(
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <AppGetIcon name="xCircleFill" />
            {errorMessage}
          </div>,
          { icon: false }
        );
      }

      setLoadingEmotions(prev => ({ ...prev, [emotionId]: false }));

    }
  };


  // Handler para eliminar logo
  const handleFileDelete = async (
    emotionId: number,
    setFieldValue: (field: string, value: any) => void,
    values: WidgetData
  ) => {
    setLoadingEmotions(prev => ({ ...prev, [emotionId]: true }));

    try {
      const emotion = values.reactions.find((e) => e.id === emotionId);
      if (!emotion?.imageUrl) return;

      const response: any = await deleteUploadReactionService.run(emotion.imageUrl);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {response?.message ?? "Archivo eliminado correctamente"}
        </div>,
        { icon: false }
      );

      const updatedEmotions = values.reactions.map((emo) =>
        emo.id === emotionId ? { ...emo, imageUrl: undefined } : emo
      );

      setFieldValue("reactions", JSON.parse(JSON.stringify(updatedEmotions)));

      // ✅ Sincronizar también la data original para que la comparación no falle
      if (isEditMode) {
        setOriginalEditData((prev) => {
          if (!prev) return prev;

          const newReactions = prev.reactions.map((emo) =>
            emo.id === emotionId ? { ...emo, imageUrl: undefined } : emo
          );

          return {
            ...prev,
            reactions: newReactions,
          };
        });
      }

    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Ocurrió un error al eliminar el archivo";

      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {errorMessage}
        </div>,
        { icon: false }
      );
    }

    setLoadingEmotions(prev => ({ ...prev, [emotionId]: false }));
  };




  return (
    <div className={style.container}>
      {/* LISTA */}
      <DataTable
        title="Lista de widgets"
        columns={columns}
        service={getWidgetsWithPaginationService}
        reloadData={isReloadData}
        addButton={openCreateModal}
        excelButton={{
          enabled: true,
          nameFile: `Lista de widgets`,
          getExcelData: (data: WidgetData[]) => {
            return data.map((row) => ({
              NOMBRE: row.name,
              WIDGET_ID: row.widgetId,
              DESCRIPCION: row.description,
              INSTITUCION: row.institution.name,
              COLOR_PRIMARIO: row.primaryColor,
              COLOR_SECUNDARIO: row.secondaryColor,
              // MENSAJE_BIENVENIDA: row.welcomeMessage,
              // MENSAJE_DESPEDIDA: row.goodbyeMessage,
              EMOCIONES: row.reactions.length,
              ESTADO: row.isActive ? "Activado" : "Desactivado",
            }));
          },
        }}
      >
        {""}
      </DataTable>

      {/* MODAL CREAR/EDITAR */}
      <Modal
        title={isEditMode ? "Editar widget" : "Crear widget"}
        isOpen={isOpen}
        onClose={() => {
          if (formikValuesRef.current) {
            handleIntentToCloseModal(formikValuesRef.current);
          }
        }}


        size={{ width: "90%", maxWidth: "1300px", height: "" }}
        loadingModal={loading}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={() => {
                if (formikValuesRef.current) {
                  handleIntentToCloseModal(formikValuesRef.current);
                }
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styleForm.buttomFooter}
              form="widgetForm"
              disabled={loading}
            >
              Guardar
            </button>
          </div>
        }

      >
        <Formik
          initialValues={formData}
          validationSchema={WidgetSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, ...formikHelpers }) => {
            // Guardar referencia actual de values para usarla en footerContent
            formikValuesRef.current = values;

            return (
              <Form id="widgetForm" className="flex-row">
                <h4 className={style.under__line}>
                  <strong>Información general:</strong>
                </h4>

                {/* Nombre / Institución */}
                <div className={styleForm.form__container_row}>
                  <InputText
                    classNameInput={styleForm.input__input}
                    type="text"
                    name="name"
                    placeholder="Nombre"
                    TextLabel="Nombre*"
                  />

                  {/* Institución - InputSearch con data local */}
                  <Field name="institutionId">
                    {({ field, form, meta }: any) => (
                      <AppInputSearch
                        serviceCall={serviceInstitutionsAllFilter}
                        classNameInput={styleForm.input__input}
                        onSelect={(selected: any) => {
                          setSelectedInstitution({
                            label: selected.label,
                            value: selected.value
                          });
                          setFieldValue("institutionId", selected.value);
                        }}

                        selected={selectedInstitution}
                        name="institutionId"
                        textLabel="Institución*"
                      />
                    )}
                  </Field>

                </div>

                {/* Descripción */}
                <div className={styleForm.form__container_row}>
                  <InputTextArea
                    name="description"
                    classNameInput={styleForm.input__inputWide}
                    placeholder="Descripción widget"
                    TextLabel="Descripción*"
                  />
                </div>

                {/* Colores */}
                <div className={styleForm.form__container_row}>
                  <Field name="primaryColor">
                    {({ field, meta }: FieldProps) => (
                      <ColorField
                        label="Color primario*"
                        {...field}
                        classNameInput={styleForm.input__input}
                        error={meta.touched && meta.error ? meta.error : undefined}
                        touched={meta.touched}
                      />
                    )}
                  </Field>

                  <Field name="secondaryColor">
                    {({ field, meta }: FieldProps) => (
                      <ColorField
                        label="Color secundario*"
                        {...field}
                        classNameInput={styleForm.input__input}
                        error={meta.touched && meta.error ? meta.error : undefined}
                        touched={meta.touched}
                      />
                    )}
                  </Field>
                </div>

                {/* Mensajes */}
                {/* <div className={styleForm.form__container_row}>
                <InputTextArea
                  name="welcomeMessage"
                  classNameInput={styleForm.input__input}
                  placeholder="Mensaje bienvenida"
                  TextLabel="Mensaje bienvenida"
                />
              </div>

              <div className={styleForm.form__container_row}>
                <InputTextArea
                  name="goodbyeMessage"
                  classNameInput={styleForm.input__input}
                  placeholder="Mensaje despedida"
                  TextLabel="Mensaje despedida"
                />
              </div> */}
                {/* Activo */}
                <div style={{ paddingTop: 10 }}>
                  <label className={style.title}>Activo</label>
                  <Field type="checkbox" name="isActive" />
                </div>
                {/* Emociones */}
                <h4 className={style.under__line}>
                  <strong>Reacciones</strong>
                </h4>

                <div style={{ marginTop: 20 }}>
                  <FieldArray name="reactions">
                    {() => (
                      <div className={style.emotions_container}>
                        {values.reactions.map((emo, idx) => {

                          const currentImg = emo.imageUrl ?? emo.defaultImageUrl;

                          return (
                            <div key={emo.id} className={style.emotion_card}>
                              <h5 className={style.emotion_title}>{emo.reaction?.name ?? emo.name}</h5>
                              <FileInput
                                name={`reactions[${idx}].file`}
                                formats={["png", "jpg", "jpeg", "svg", "gif"]}
                                logoUrl={currentImg}
                                loading={Boolean(loadingEmotions[emo.id])}
                                onChange={(e) => handleFileUpload(e, emo.id, setFieldValue, values)}
                                onDeleteLogo={() => handleFileDelete(emo.id, setFieldValue, values)}
                                isDeletable={Boolean(emo.imageUrl && emo.imageUrl !== emo.defaultImageUrl)} // ✅ aquí está corregido
                              />
                              <InputTextArea
                                name={`reactions[${idx}].message`}
                                classNameInput={styleForm.input__inputWide}
                                placeholder="Mensaje"
                                TextLabel="Mensaje*"
                              />

                            </div>
                          );
                        })}
                      </div>
                    )}
                  </FieldArray>


                </div>
              </Form>
            );
          }}
        </Formik>

      </Modal>

      {/* MODAL DELETE */}
      {/* ✅ Modal de confirmación de eliminado (reemplaza el <Modal> anterior) */}
      <DeleteConfirmationModal
        isOpen={Boolean(isOpenCurrentDelete)}
        onClose={handleCloseModalDelete}
        onConfirm={handleDelete}
        loading={loading}
        itemName={isOpenCurrentDelete?.name}
        customMessage="¿Está seguro que desea eliminar este widget?"
      />

      <Modal
        title="Confirmar salida"
        isOpen={showConfirmExitModal}
        onClose={handleCancelExit}
        size={{ width: "500px", height: "" }}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={handleCancelExit}
            >
              Cancelar
            </button>
            <button
              className={styleForm.buttomFooter}
              onClick={handleExitAndDeleteUploaded}
            >
              Sí, salir y eliminar imágenes
            </button>
          </div>
        }
      >
        <div style={{ padding: 30 }}>
          <center>
            Has subido una o más imágenes.<br />
            ¿Estás seguro que deseas salir? Se eliminarán esas imágenes si continúas.
          </center>
        </div>
      </Modal>
      <Modal
        title="Generar HTML de incrustación"
        isOpen={isOpenHtml}
        onClose={() => setIsOpenHtml(false)}
        size={{ width: "90%", maxWidth: "1000px", height: "" }}
        footerContent={
          <div className={style.containerButtoms}>
            <button
              className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar}
              onClick={() => setIsOpenHtml(false)}
            >
              Cerrar
            </button>
            <button
              className={styleForm.buttomFooter}
              onClick={copyHtmlToClipboard}
            >
              Copiar HTML
            </button>
            {/* <button
              className={styleForm.buttomFooter}
              onClick={downloadHtml}
            >
              Descargar index.html
            </button> */}
          </div>
        }
      >
          <h4 className={style.under__line}>
             <strong>Cómo integrar el asistente Robi en tu sitio:</strong>
          </h4>
          <ol  style={{
            marginLeft: 20,
            marginBottom: 20,
            color: "var(--color-on-surface-variant-main)",
          }}>
            <li>Presiona <strong>Copiar HTML</strong> para copiar el código de abajo.</li>
            <li>Pega ese contenido en un archivo <code>index.html</code> o en el bloque HTML de tu página.</li>
            <li>
              Verifica que se incluyan:
              <ul>
                <li>El <strong>CSS</strong> en la etiqueta <code>&lt;head&gt;</code></li>
                <li>El contenedor <code>&lt;div id="my-widget"&gt;&lt;/div&gt;</code> dentro del <code>&lt;body&gt;</code></li>
                <li>El <strong>script</strong> y la llamada a <code>initChatbot</code> antes de <code>&lt;/body&gt;</code></li>
              </ul>
            </li>
            <li>Guarda y publica tu sitio. El asistente quedará disponible de inmediato</li>
          </ol>

          <p style={{ marginBottom: 10,
             color: "var(--color-on-surface-variant-main)",
           }}>
            Copia y pega este archivo <strong>index.html</strong> en cualquier sitio estático:
          </p>
          <textarea
            readOnly
            value={htmlPreview}
            style={{
              width: "100%",
              minHeight: 400,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 13,
              lineHeight: "1.45",
              padding: 12,
              borderRadius: 6,
              border: "1px solid #e4e7f1",
              background: "#0b1020",
              color: "#d7e1ff",
              resize: "vertical",
              whiteSpace: "pre",
            }}
          />
      </Modal>



    </div>
  );
}
